import { useCallback, type ComponentProps, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Field, FieldLabel, useFieldControl } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";

/**
 * The building blocks every page is made of, so all pages agree on where
 * the title, the actions and the tabs live and how much air sits between them.
 */

/** The padded column a page's content sits in. */
export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("mx-auto flex w-full max-w-[1400px] flex-col gap-5 p-4 md:p-6", className)}>{children}</div>
  );
}

/** Title, one sentence of context, the page's actions, and anything that belongs under the title (tabs, filters). */
export function PageHead({
  title,
  description,
  actions,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
          {description && <p className="text-muted-foreground mt-1 text-sm text-pretty">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

/**
 * A titled block that is not a Card. Kit components like DataGrid draw their
 * own surface; wrapping them in a Card gives two borders. Use Card only for
 * content that draws nothing of its own.
 */
export function Section({
  title,
  description,
  action,
  children,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("flex min-w-0 flex-col gap-3", className)}>
      {(title || action) && (
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div className="min-w-0">
            {title && <h2 className="text-sm font-medium">{title}</h2>}
            {description && <p className="text-muted-foreground mt-0.5 text-xs">{description}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export type RouteTab = { to: string; label: ReactNode; count?: number; hidden?: boolean };

/**
 * Tabs that are URLs. Each tab is a real route, so a tab can be linked to,
 * reloaded and reached with Back -- a page that forgets which tab it was on
 * after a refresh is the thing tabbed pages usually get wrong.
 *
 * `to` is relative to the page (e.g. "overview"), or "" for the index tab.
 */
export function RouteTabs({ base, tabs }: { base: string; tabs: RouteTab[] }) {
  // The query string rides along: a page's period lives in `?start=&end=`, and
  // switching tabs must not quietly reset it.
  const { pathname, search } = useLocation();
  const visible = tabs.filter((t) => !t.hidden);

  return (
    <div
      className="border-border -mb-px flex gap-1 overflow-x-auto border-b [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      role="tablist"
    >
      {visible.map((tab) => {
        const href = tab.to ? `${base}/${tab.to}` : base;
        const active = tab.to
          ? pathname === href || pathname.startsWith(`${href}/`)
          : pathname === (base || "/") || pathname === `${base}/`;
        return (
          <NavLink
            key={href}
            to={`${href || "/"}${search}`}
            role="tab"
            aria-selected={active}
            className={cn(
              "relative flex h-10 shrink-0 items-center gap-1.5 px-3 text-sm font-medium whitespace-nowrap transition-colors",
              active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="bg-secondary text-secondary-foreground rounded-full px-1.5 py-0.5 text-[10px] tabular-nums">
                {tab.count}
              </span>
            )}
            {active && <span className="bg-primary absolute inset-x-2 -bottom-px h-0.5 rounded-full" />}
          </NavLink>
        );
      })}
    </div>
  );
}

/** `useToast` with the three calls pages actually make. */
export function useNotify() {
  const { toast } = useToast();
  return useCallback(
    (title: string, description?: string, color: "neutral" | "green" | "destructive" = "neutral") =>
      toast({ title, description, color }),
    [toast],
  );
}

function FieldInput(props: ComponentProps<typeof Input>) {
  return <Input {...useFieldControl()} {...props} />;
}

/** A labelled input with id, description and error wired through Field. */
export function TextField({
  label,
  description,
  error,
  required,
  ...props
}: Omit<ComponentProps<typeof Input>, "error"> & {
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
}) {
  return (
    <Field description={description} error={error}>
      <FieldLabel required={required}>{label}</FieldLabel>
      <FieldInput error={Boolean(error)} {...props} />
    </Field>
  );
}

function FieldTextarea(props: ComponentProps<typeof Textarea>) {
  return <Textarea {...useFieldControl()} {...props} />;
}

export function TextareaField({
  label,
  description,
  error,
  required,
  ...props
}: Omit<ComponentProps<typeof Textarea>, "error"> & {
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
}) {
  return (
    <Field description={description} error={error}>
      <FieldLabel required={required}>{label}</FieldLabel>
      <FieldTextarea error={Boolean(error)} {...props} />
    </Field>
  );
}

function FieldSelect(props: ComponentProps<typeof Select>) {
  const { id, ...aria } = useFieldControl();
  return <Select {...props} id={id} {...aria} />;
}

export function SelectField({
  label,
  description,
  error,
  required,
  ...props
}: Omit<ComponentProps<typeof Select>, "error"> & {
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
}) {
  return (
    <Field description={description} error={error}>
      <FieldLabel required={required}>{label}</FieldLabel>
      <FieldSelect error={Boolean(error)} triggerLabel={typeof label === "string" ? label : undefined} {...props} />
    </Field>
  );
}

/** Label / value rows for a detail card. */
export function KeyValue({ items }: { items: { label: ReactNode; value: ReactNode }[] }) {
  return (
    <dl className="grid gap-2 text-sm">
      {items.map((item, i) => (
        <div key={i} className="flex items-baseline justify-between gap-3">
          <dt className="text-muted-foreground">{item.label}</dt>
          <dd className="min-w-0 text-end font-medium tabular-nums">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
